import { createApi } from 'ReduxHelpers';
import { getBackRoyalApiBaseConfig } from 'BackRoyalApi';
import { type BaseQueryApi } from '@reduxjs/toolkit/query';
import { uploadFiles, type PublishMeta, type QueryFnBaseQuery } from 'ContentItemEditor';
import { type CohortCourseSummary, type CohortRecord, type CohortUpdateFields } from './AdminEditCohort.types';

const COLLECTION = 'cohorts';

/*
    A cohort save republishes the cohort and requeues work for every included student, so it runs
    far longer than a read. A falsy timeout attaches no timeout signal, which opts out of the
    shared 30 second default, as the Angular $http path did.
*/
const SAVE_TIMEOUT = 0;

/*
    The casing conversion is off, because the editor PUTs back the record it received. A cohort
    carries data-shaped keys, such as the names inside `exercise_message_variables`, which the
    converter would rewrite as though they were field names.
*/
const baseConfig = getBackRoyalApiBaseConfig('adminEditCohortApi', { convertCasing: false });

type CohortUpdateResponse = { contents?: { cohorts?: CohortUpdateFields[] } };
type CohortCreateResponse = { contents?: { cohorts?: CohortRecord[] } };
type CourseSummaryResponse = { contents?: { course_summary?: CohortCourseSummary } };

export const adminEditCohortApi = createApi('adminEditCohortApi', {
    ...baseConfig,
    endpoints: builder => ({
        /*
            PUT carries no id in the path. The id travels in record.id, which is the route the
            Iguana adapter used.
        */
        updateCohort: builder.mutation<CohortUpdateFields, { record: CohortRecord; meta: PublishMeta }>({
            query: ({ record, meta }) => ({
                url: `${COLLECTION}.json`,
                method: 'PUT',
                body: { record, meta },
                timeout: SAVE_TIMEOUT,
            }),

            /*
                An empty fallback would keep the loaded updated_at as the new baseline, and the
                next save would then tell the admin that someone else saved the cohort, which is
                false. UPDATE_FIELDS always comes back, so a missing body means a real server
                change.
            */
            transformResponse: (response: CohortUpdateResponse) => {
                const savedFields = response?.contents?.cohorts?.[0];
                if (!savedFields) throw new Error('Unexpected response shape from the cohort save');
                return savedFields;
            },

            /*
                The shared base query retries once on a disconnect or a 502/504, and RTK applies
                that to mutations too. A retried save publishes a second version and then fails the
                updated_at check. The Angular $http path never retried.
            */
            extraOptions: { maxRetries: 0 },
        }),

        // A new cohort answers with the whole ADMIN_FIELDS record, not with UPDATE_FIELDS.
        createCohort: builder.mutation<CohortRecord, { record: CohortRecord; meta: PublishMeta }>({
            query: ({ record, meta }) => ({
                url: `${COLLECTION}.json`,
                method: 'POST',
                body: { record, meta },
                timeout: SAVE_TIMEOUT,
            }),
            transformResponse: (response: CohortCreateResponse) => {
                const created = response?.contents?.cohorts?.[0];
                if (!created) throw new Error('Unexpected response shape from the cohort create');
                return created;
            },

            // A retry would create a second cohort.
            extraOptions: { maxRetries: 0 },
        }),

        destroyCohort: builder.mutation<void, string>({
            query: id => ({ url: `${COLLECTION}/${id}.json`, method: 'DELETE' }),
            extraOptions: { maxRetries: 0 },
        }),

        // A template uploads through the cohorts endpoint too. It only stores the file and answers
        // with it; neither record is touched until the save.
        uploadCourseSummary: builder.mutation<CohortCourseSummary, File>({
            async queryFn(file, _api: BaseQueryApi, _options, fetchWithBaseQuery: QueryFnBaseQuery) {
                const result = await uploadFiles<CourseSummaryResponse>(
                    { url: `${COLLECTION}/upload_course_summary.json`, key: 'file', files: [file] },
                    fetchWithBaseQuery,
                );

                if (result.error) return { error: result.error };

                const courseSummary = result.data?.contents?.course_summary;
                if (!courseSummary) {
                    return { error: { status: 'CUSTOM_ERROR' as const, error: 'The course summary was not returned' } };
                }

                return { data: courseSummary };
            },
            extraOptions: { maxRetries: 0 },
        }),
    }),
});
